"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { YOUZU_API_BASE_URL } from "@/constants/api";
import { createAccessToken } from "@/services/token.service";

interface LightingState {
  intensity: number;
  exposure: number;
  background: "light" | "neutral" | "dark";
}

const PRESETS = [
  { id: "studio", label: "Studio" },
  { id: "bright", label: "Bright" },
  { id: "soft", label: "Soft" },
  { id: "dramatic", label: "Dramatic" },
] as const;

const BG_OPTIONS: { id: LightingState["background"]; label: string }[] = [
  { id: "light", label: "Light" },
  { id: "neutral", label: "Neutral" },
  { id: "dark", label: "Dark" },
];

interface TwoDToThreeDSDKProps {
  catalogueId?: string;
}

export function TwoDToThreeDSDK({ catalogueId }: TwoDToThreeDSDKProps) {
  const sdkRef = useRef<any>(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [lighting, setLighting] = useState<LightingState>({
    intensity: 1.5,
    exposure: 1.5,
    background: "light",
  });

  const callSDK = useCallback(
    (fn: (el: any) => void) => {
      if (sdkRef.current) fn(sdkRef.current);
    },
    []
  );

  const handleIntensityChange = (value: number[]) => {
    const intensity = value[0];
    setLighting((prev) => ({ ...prev, intensity }));
    callSDK((el) => el.setLightingIntensity?.(intensity));
  };

  const handleExposureChange = (value: number[]) => {
    const exposure = value[0];
    setLighting((prev) => ({ ...prev, exposure }));
    callSDK((el) => el.setExposure?.(exposure));
  };

  const handleBackground = (bg: LightingState["background"]) => {
    setLighting((prev) => ({ ...prev, background: bg }));
    callSDK((el) => el.setBackgroundColor?.(bg));
  };

  const handlePreset = (preset: string) => {
    callSDK((el) => {
      el.applyLightingPreset?.(preset);
      const state = el.getLightingState?.();
      if (state) setLighting(state);
    });
  };

  useEffect(() => {
    const element = sdkRef.current;
    if (!element) return;

    element.resolveToken = async () => {
      try {
        return await createAccessToken({ catalogueId: catalogueId || undefined });
      } catch (error) {
        console.error("❌ Failed to resolve token:", error);
        return null;
      }
    };

    const handleJobCreated = (e: CustomEvent) => console.log("📦 Job created:", e.detail);
    const handleModelLoaded = (e: CustomEvent) => {
      console.log("🎉 Model loaded:", e.detail);
      setModelLoaded(true);
      // Sync initial lighting state from SDK
      const state = element.getLightingState?.();
      if (state) setLighting(state);
    };

    element.addEventListener("jobCreated", handleJobCreated);
    element.addEventListener("modelLoaded", handleModelLoaded);

    return () => {
      element.removeEventListener("jobCreated", handleJobCreated);
      element.removeEventListener("modelLoaded", handleModelLoaded);
    };
  }, [catalogueId]);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0 overflow-hidden">
          <div className="w-full" style={{ minHeight: "700px" }}>
            <youzu-2d-to-3d
              ref={sdkRef}
              api-base-url={YOUZU_API_BASE_URL}
              style={{
                display: "block",
                minHeight: "700px",
                width: "100%",
              }}
            />
          </div>
        </CardContent>
      </Card>

      {modelLoaded && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">💡 Lighting Controls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Presets */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Presets</p>
              <div className="flex gap-2">
                {PRESETS.map((p) => (
                  <Button
                    key={p.id}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => handlePreset(p.id)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Brightness */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground font-medium">Brightness</p>
                <span className="text-xs text-muted-foreground">{Math.round(lighting.intensity * 100)}%</span>
              </div>
              <input
                type="range"
                min={0.5}
                max={4}
                step={0.1}
                value={lighting.intensity}
                onChange={(e) => handleIntensityChange([parseFloat(e.target.value)])}
                className="w-full accent-primary"
              />
            </div>

            {/* Exposure */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground font-medium">Exposure</p>
                <span className="text-xs text-muted-foreground">{Math.round(lighting.exposure * 100)}%</span>
              </div>
              <input
                type="range"
                min={0.5}
                max={3}
                step={0.1}
                value={lighting.exposure}
                onChange={(e) => handleExposureChange([parseFloat(e.target.value)])}
                className="w-full accent-primary"
              />
            </div>

            {/* Background */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Background</p>
              <div className="flex gap-2">
                {BG_OPTIONS.map((bg) => (
                  <Button
                    key={bg.id}
                    variant={lighting.background === bg.id ? "default" : "outline"}
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => handleBackground(bg.id)}
                  >
                    {bg.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
